import React, { useState } from 'react';
import { Image as ImageIcon, ExternalLink, RefreshCw, AlertCircle, ChevronDown, ChevronRight, Film, LayoutGrid, Sparkles, Maximize2 } from 'lucide-react';
import { ImageLightbox } from '../team-chat/ImageLightbox';
import { useAdCreative, CTA_LABELS, TYPE_LABELS, type AdCreative } from '../../lib/hooks/useAdCreative';

/**
 * Anteprima compatta della creatività dell'annuncio Meta, dentro la riga Origine
 * del pannello lead.
 *
 * Sta chiusa: miniatura, titolo e due righe di testo. Aperta mostra il copy intero
 * dentro un'area a scorrimento, così un annuncio con venti righe di testo non
 * spinge fuori schermo il resto del pannello. La miniatura si ingrandisce nel
 * visualizzatore condiviso.
 *
 * Il recupero del dato sta in `useAdCreative`, condiviso con il blocco "lead
 * arrivato" della cronologia.
 */

interface Props {
  leadId: string;
  adId: string;
  organizationId: string;
  adName?: string | null;
}

const TYPE_ICONS: Record<string, React.ElementType> = {
  video: Film,
  carousel: LayoutGrid,
  dynamic: Sparkles,
  image: ImageIcon,
};

function Skeleton() {
  return (
    <div className="flex gap-2.5 px-2 py-2" aria-label="Caricamento creatività">
      <div className="h-11 w-11 flex-shrink-0 animate-pulse rounded-md bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.06]" />
      <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
        <div className="h-3 w-2/5 animate-pulse rounded bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.06]" />
        <div className="h-2.5 w-full animate-pulse rounded bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.04]" />
      </div>
    </div>
  );
}

/** Chip del tipo di annuncio e dell'invito all'azione, comuni ai due formati. */
export function CreativeChips({ creative }: { creative: AdCreative }) {
  const TipoIcon = creative.creativeType ? TYPE_ICONS[creative.creativeType] : undefined;
  const tipo = creative.creativeType ? TYPE_LABELS[creative.creativeType] : null;
  const cta = creative.callToAction ? CTA_LABELS[creative.callToAction] || creative.callToAction : null;
  if (!tipo && !cta && !creative.previewLink) return null;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {tipo && TipoIcon && (
        <span className="inline-flex items-center gap-1 rounded bg-[rgba(55,53,47,0.06)] px-1.5 py-0.5 text-[10px] text-[#787774] dark:bg-white/[0.06] dark:text-gray-400">
          <TipoIcon className="h-2.5 w-2.5" />
          {tipo}
        </span>
      )}
      {cta && (
        <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          {cta}
        </span>
      )}
      {creative.previewLink && (
        <a
          href={creative.previewLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-[10px] text-[#2383E2] hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          Anteprima <ExternalLink className="h-2.5 w-2.5" />
        </a>
      )}
    </div>
  );
}

export function AdCreativePreview({ leadId, adId, organizationId, adName }: Props) {
  const { creative, loading, enriching, reason } = useAdCreative(leadId, adId, organizationId);
  const [aperto, setAperto] = useState(false);
  const [ingrandita, setIngrandita] = useState(false);
  const [immagineRotta, setImmagineRotta] = useState(false);

  if (loading) return <Skeleton />;

  const niente = !creative?.imageUrl && !creative?.headline && !creative?.body;
  if (niente && !enriching) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-[#787774] dark:text-gray-400">
        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
        <span>
          {reason === 'meta_non_collegato'
            ? 'Creatività non disponibile: account Meta da ricollegare'
            : 'Creatività non disponibile per questo annuncio'}
        </span>
      </div>
    );
  }

  const testo = creative?.body || '';
  const mostraImmagine = creative?.imageUrl && !immagineRotta;

  return (
    <div className="rounded-md border border-[rgba(55,53,47,0.09)] bg-[#FBFBFA] dark:border-white/[0.08] dark:bg-white/[0.02]">
      <div className="flex gap-2.5 px-2 py-2">
        {mostraImmagine ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIngrandita(true); }}
            className="group relative h-11 w-11 flex-shrink-0 overflow-hidden rounded-md border border-[rgba(55,53,47,0.09)] dark:border-white/[0.08]"
            title="Ingrandisci la creatività"
          >
            <img
              src={creative!.imageUrl!}
              alt={adName || 'Creatività'}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={() => setImmagineRotta(true)}
            />
            <span className="absolute inset-0 hidden items-center justify-center bg-black/35 group-hover:flex">
              <Maximize2 className="h-3.5 w-3.5 text-white" />
            </span>
          </button>
        ) : (
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md border border-dashed border-[rgba(55,53,47,0.14)] dark:border-white/[0.1]">
            <ImageIcon className="h-4 w-4 text-[#B9B9B7]" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          {creative?.headline && (
            <div className="truncate text-[13px] font-medium leading-snug text-[#37352F] dark:text-white" title={creative.headline}>
              {creative.headline}
            </div>
          )}

          {testo ? (
            <div
              className={`mt-0.5 whitespace-pre-line text-[12px] leading-relaxed text-[rgba(55,53,47,0.7)] dark:text-gray-400 ${
                aperto ? 'max-h-44 overflow-y-auto pr-1' : 'line-clamp-2'
              }`}
            >
              {testo}
            </div>
          ) : enriching ? (
            <div className="mt-1 flex items-center gap-1.5 text-[12px] text-[#787774] dark:text-gray-400">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Recupero il testo da Meta…
            </div>
          ) : (
            <div className="mt-1 text-[12px] text-[#787774] dark:text-gray-400">
              {reason === 'meta_non_collegato' ? 'Testo non disponibile: account Meta da ricollegare' : 'Testo non disponibile'}
            </div>
          )}

          {aperto && creative && <CreativeChips creative={creative} />}

          {(testo || creative?.previewLink) && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setAperto(!aperto); }}
              className="mt-1 inline-flex items-center gap-0.5 text-[11px] text-[#787774] hover:text-[#37352F] dark:text-gray-400 dark:hover:text-white"
            >
              {aperto ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {aperto ? 'riduci' : 'mostra tutto'}
            </button>
          )}
        </div>
      </div>

      {ingrandita && creative?.imageUrl && (
        <ImageLightbox
          images={[{ url: creative.imageUrl, name: adName || creative.headline || 'Creatività' }]}
          initialIndex={0}
          onClose={() => setIngrandita(false)}
        />
      )}
    </div>
  );
}
